import xeVisuals from "../../../../CaseStudies/XEVisuals";

const FEATURED_VISUAL_INDEXES = new Set([12, 7, 0, 13, 23]);

const currencyApp = {
  "id": "currency-app",
  "slug": "currency-app",
  "category": "Applications",
  "navTitle": "Evolving the XE Currency App",
  "title": "Evolving the XE Currency App To 80+ Million Installs",
  "introduction": [
    "The XE Currency App began as a simple one-page currency converter. As mobile use grew and customer expectations changed, I led its evolution into a broader global product with rate tracking, personalized tools, cross-platform experiences, and access to XE’s expanding financial services.",
    "Over time, the app reached more than 80 million installs and maintained a 4.5-star rating across app stores."
  ],
  "challenge": [
    {
      "title": "Growing customer expectations",
      "paragraphs": [
        "Users increasingly expected more than a quick conversion. They wanted to follow exchange rates, return to frequently used currencies, and act when market conditions changed."
      ]
    },
    {
      "title": "Protecting simplicity",
      "paragraphs": [
        "The app’s speed and ease of use were central to its success. New features had to add value without making a simple conversion task feel complicated."
      ]
    },
    {
      "title": "A global, cross-platform audience",
      "paragraphs": [
        "The experience needed to remain reliable and understandable across countries, devices, operating systems, accessibility needs, and different levels of financial knowledge."
      ]
    },
    {
      "title": "An expanding business",
      "paragraphs": [
        "As XE introduced services such as international money transfer, the app needed to support a broader customer journey without losing the trust associated with its core currency tools."
      ]
    }
  ],
  "process": [
    {
      "title": "Understanding what users needed next",
      "paragraphs": [
        "I used research, analytics, app-store feedback, and customer conversations to identify where the existing experience could provide more value."
      ],
      "bullets": [
        "Studied how people monitored currencies before travelling, transferring money, or making financial decisions.",
        "Identified repeated behaviours such as checking the same currencies and waiting for a particular rate.",
        "Used customer evidence and business priorities to shape an evolving product roadmap.",
        "Prioritized additions that extended the app’s usefulness rather than adding features simply to match competitors."
      ],
      "closing": "This gave us a clearer direction for evolving the product while protecting what users already valued."
    },
    {
      "title": "Expanding the product without losing simplicity",
      "paragraphs": [
        "We introduced features gradually and kept the core conversion experience easy to access."
      ],
      "bullets": [
        "Created personalized currency profiles for frequently used conversions.",
        "Introduced Rate Alerts so users could choose a target exchange rate and be notified when it was reached.",
        "Used clear hierarchy and progressive disclosure to keep more advanced functions from overwhelming the primary task.",
        "Connected relevant parts of the app to XE’s money-transfer service as the business expanded."
      ],
      "closing": "Rate Alerts became one of the app’s most valuable engagement features, with more than two million alerts created each month."
    },
    {
      "title": "Designing across platforms",
      "paragraphs": [
        "The app needed to feel consistent without ignoring the strengths and conventions of each device."
      ],
      "bullets": [
        "Worked closely with engineering teams to adapt the experience across mobile phones, tablets, wearables, and other platforms.",
        "Collaborated with Apple and Microsoft teams on experiences for products including Apple Watch and Windows devices.",
        "Maintained recognizable interaction patterns while adapting layouts and behaviour to each platform.",
        "Balanced feature consistency with performance, screen size, and device-specific use cases."
      ]
    },
    {
      "title": "Improving through evidence",
      "paragraphs": [
        "The product continued to evolve after each release."
      ],
      "bullets": [
        "Used analytics to understand feature adoption, engagement, and where users encountered friction.",
        "Reviewed customer feedback and app-store responses to identify recurring needs.",
        "Tested new interactions and refined existing features rather than relying on large, infrequent redesigns.",
        "Improved accessibility and usability as the product expanded to serve a broader audience."
      ],
      "closing": "The approach was continuous improvement rather than treating the app as a finished product."
    }
  ],
  "outcomes": [
    {
      "title": "Global product adoption",
      "description": "The XE Currency App grew to more than 80 million installs, establishing it as one of the leading products in its category."
    },
    {
      "title": "Strong customer trust",
      "description": "The app maintained a 4.5-star rating across app stores while its features and supported platforms continued to expand."
    },
    {
      "title": "Greater engagement",
      "description": "Rate Alerts, personalized currency profiles, and richer financial tools gave users reasons to return beyond one-time currency conversion."
    },
    {
      "title": "A broader customer journey",
      "description": "The app evolved from a standalone utility into an important part of XE’s wider digital ecosystem, connecting trusted currency information with additional financial services."
    },
    {
      "title": "Sustainable product evolution",
      "description": "The team was able to introduce new capabilities incrementally while protecting the speed, clarity, and reliability that made the original app successful."
    }
  ],
  "reflection": [
    "The app reinforced that a successful product does not always need to be reinvented. Often, the better approach is to understand what customers already value, anticipate what they will need next, and evolve the experience without disrupting the trust that made it successful.",
    "As XE expanded, every new feature had to earn its place. The challenge was deciding what genuinely extended the product, introducing it at the right time, and keeping the experience intuitive as both the app and the business grew more complex.",
    "That balance between continuity and change became an important part of how I approached product modernization throughout my time at XE."
  ],
  "media": {
    "blocks": [
      {
        "id": "currency-app-featured",
        "type": "carousel",
        "placement": "afterIntro",
        "items": xeVisuals.filter((_, index) =>
          FEATURED_VISUAL_INDEXES.has(index)
        ),
        "layout": "mobileFocus",
        "options": {
          "frameMaxHeight": "min(64vh, 720px)"
        }
      },
      {
        "id": "currency-app-interactive-prototype",
        "type": "prototype",
        "placement": "afterApproach",
        "title": "Explore the original XE Currency App",
        "description": "Open the preserved interactive prototype to explore the product flow and screen relationships.",
        "thumbnail": xeVisuals[7].src,
        "thumbnailAlt": "XE Currency App prototype screen",
        "prototypeSrc": "/prototypes/xe/currency-app/index.html",
        "launchLabel": "Open interactive prototype"
      }
    ]
  }
};

export default currencyApp;
